import ApiService from "./ApiService";
import Logger from "./Logger";

/**
 * Preload and play bundled UI sound effects (CRA `require` → resolved URLs).
 * Game SFX are AAC `.m4a` from the cinematic pack (see `resources/sounds/mp4/HowToUse.md`).
 */
const EFFECT_SRC = {
    slidingMove: require("../assets/sounds/effect/sliding-move.m4a"),
    slidingCellMatch: require("../assets/sounds/effect/sliding-cell-match.m4a"),
    // Row/column complete — ringtone bell (no sliding-bonus “hum” layered after).
    slidingLineMatch: require("../assets/sounds/effect/universfield-ringtone-06-153265.mp3"),
    slidingBonus: require("../assets/sounds/effect/sliding-bonus.m4a"),
    // Soft placement sweep; old jigsaw-piece-snap.m4a was harsh/scratchy.
    jigsawPieceSnap: require("../assets/sounds/effect/mixkit-fast-sweep-transition-174.mp3"),
    wordResolved: require("../assets/sounds/effect/word-resolved.m4a"),
    applause: require("../assets/sounds/applause-sound-effect-240470.mp3"),
};

/** Optional playback caps: skip extra sounds on rapid taps, cut a long tail. */
const EFFECT_PLAY = {
    slidingMove: { durationSeconds: 0.17, skipIfPlaying: true, minIntervalMs: 280 },
    slidingCellMatch: { durationSeconds: 0.35, skipIfPlaying: true },
    slidingLineMatch: { durationSeconds: 1.4, skipIfPlaying: true, volume: 0.85 },
    slidingBonus: { durationSeconds: 0.5, skipIfPlaying: true },
    jigsawPieceSnap: {
        durationSeconds: 0.35,
        skipIfPlaying: true,
        minIntervalMs: 90,
        volume: 0.55,
    },
    wordResolved: { durationSeconds: 0.35, skipIfPlaying: true },
};

/** @type {Map<string, HTMLAudioElement>} */
const effectCache = new Map();
/** @type {Map<string, number>} */
const lastPlayAt = new Map();
/** @type {Map<string, number>} */
const stopTimers = new Map();

/** One shared BGM element so mute / navigation cannot leave a second loop running. */
let bgmEl = null;
let bgmGen = 0;
let bgmWanted = false;
let bgmLifecycleBound = false;

function haltBgmElement(el) {
    if (!el) return;
    try {
        el.onended = null;
        el.onerror = null;
        el.muted = true;
        el.pause();
        el.removeAttribute("src");
        el.src = "";
        el.load();
    } catch {
        /* some browsers throw once src is already cleared */
    }
}

function stopBgm() {
    bgmWanted = false;
    bgmGen += 1;
    const el = bgmEl;
    bgmEl = null;
    haltBgmElement(el);
}

function pauseBgm() {
    if (!bgmEl) return;
    try {
        bgmEl.pause();
    } catch {
        /* ignore */
    }
}

function resumeBgm() {
    if (!bgmWanted || !bgmEl) return Promise.resolve();
    return bgmEl.play().catch(() => {});
}

function isBgmPlaying() {
    return Boolean(bgmEl && !bgmEl.paused && !bgmEl.ended);
}

function bindBgmLifecycle() {
    if (bgmLifecycleBound || typeof window === "undefined") return;
    bgmLifecycleBound = true;
    const halt = () => stopBgm();
    window.addEventListener("pagehide", halt);
    window.addEventListener("beforeunload", halt);
    document.addEventListener("visibilitychange", () => {
        if (document.hidden) {
            pauseBgm();
            return;
        }
        if (bgmWanted) void resumeBgm();
    });
}

/**
 * @param {string} url
 * @param {{ loop?: boolean, onEnded?: () => void }} [opts]
 */
function playBgm(url, opts = {}) {
    bindBgmLifecycle();
    stopBgm();
    if (!url) return;
    bgmWanted = true;
    const gen = bgmGen;
    const el = new Audio();
    el.preload = "auto";
    el.loop = Boolean(opts.loop);
    el.src = url;
    if (typeof opts.onEnded === "function") {
        el.onended = () => {
            if (gen !== bgmGen || !bgmWanted) return;
            opts.onEnded();
        };
    }
    bgmEl = el;
    const played = el.play();
    if (played && typeof played.then === "function") {
        played
            .then(() => {
                if (gen !== bgmGen || !bgmWanted) haltBgmElement(el);
            })
            .catch(() => {});
    }
}

function clearStopTimer(effectKey) {
    const id = stopTimers.get(effectKey);
    if (id == null) return;
    window.clearTimeout(id);
    stopTimers.delete(effectKey);
}

const LIBRARY_BASE = "/home/audio";

function parseEnvelope(json) {
    if (json?.code === "FAILURE") {
        return { success: false, error: json.message || "Request failed" };
    }
    if (json?.code === "NO_DATA") {
        return { success: false, code: "NO_DATA", error: json.message || "Not found" };
    }
    return { success: true, data: json };
}

/** Spring `Page` or nested `page` object (see sample `audio.json`). */
function normalizeListResponse(response) {
    const content = response?.content ?? [];
    const pageMeta = response?.page;
    return {
        content,
        totalElements: response?.totalElements ?? pageMeta?.totalElements ?? content.length,
        totalPages: response?.totalPages ?? pageMeta?.totalPages ?? 0,
        number: response?.number ?? pageMeta?.number ?? 0,
        size: response?.size ?? pageMeta?.size ?? content.length,
    };
}

/**
 * Row is playable when it has a URL and is not explicitly disabled.
 * Omits `enable` (older API rows) ⇒ playable.
 */
export function isAudioRowPlayable(row) {
    if (!row?.url || !String(row.url).trim()) return false;
    const v = row?.enable;
    if (v === undefined || v === null) return true;
    if (v === false || v === "false" || v === 0 || v === "0") return false;
    return v === true || v === "true" || v === 1 || v === "1";
}

const AudioService = {
    /**
     * @param {keyof typeof EFFECT_SRC} effectKey
     * @returns {HTMLAudioElement | undefined}
     */
    preload(effectKey) {
        const src = EFFECT_SRC[effectKey];
        if (!src) return undefined;
        let audio = effectCache.get(effectKey);
        // HMR / remap can change EFFECT_SRC while a cached element still points at the old file.
        if (audio && audio.dataset?.effectSrc !== String(src)) {
            clearStopTimer(effectKey);
            try {
                audio.pause();
            } catch {
                /* ignore */
            }
            effectCache.delete(effectKey);
            audio = undefined;
        }
        if (audio) return audio;
        audio = new Audio(src);
        audio.preload = "auto";
        audio.dataset.effectSrc = String(src);
        const opts = EFFECT_PLAY[effectKey] || {};
        if (typeof opts.volume === "number") {
            audio.volume = Math.min(1, Math.max(0, opts.volume));
        }
        effectCache.set(effectKey, audio);
        return audio;
    },

    /**
     * @param {keyof typeof EFFECT_SRC} effectKey
     * @returns {Promise<void>}
     */
    play(effectKey) {
        const audio = this.preload(effectKey);
        if (!audio) return Promise.resolve();
        const opts = EFFECT_PLAY[effectKey] || {};
        const now = Date.now();
        if (opts.minIntervalMs) {
            const last = lastPlayAt.get(effectKey) || 0;
            if (now - last < opts.minIntervalMs) return Promise.resolve();
        }
        if (
            opts.skipIfPlaying &&
            !audio.paused &&
            !audio.ended &&
            audio.currentTime > 0
        ) {
            return Promise.resolve();
        }
        clearStopTimer(effectKey);
        audio.pause();
        audio.currentTime = 0;
        if (typeof opts.volume === "number") {
            audio.volume = Math.min(1, Math.max(0, opts.volume));
        }
        lastPlayAt.set(effectKey, now);
        const played = audio.play().catch(() => {});
        if (opts.durationSeconds > 0) {
            const ms = Math.round(opts.durationSeconds * 1000);
            stopTimers.set(
                effectKey,
                window.setTimeout(() => {
                    audio.pause();
                    audio.currentTime = 0;
                    stopTimers.delete(effectKey);
                }, ms),
            );
        }
        return played;
    },

    /**
     * @param {keyof typeof EFFECT_SRC} effectKey
     */
    release(effectKey) {
        const audio = effectCache.get(effectKey);
        if (!audio) return;
        clearStopTimer(effectKey);
        audio.pause();
        effectCache.delete(effectKey);
    },

    releaseAll() {
        stopTimers.forEach((id) => window.clearTimeout(id));
        stopTimers.clear();
        lastPlayAt.clear();
        effectCache.forEach((audio) => audio.pause());
        effectCache.clear();
        stopBgm();
    },

    playBgm,
    stopBgm,
    pauseBgm,
    resumeBgm,
    isBgmPlaying,

    /**
     * GET /home/audio — paginated library (enabled rows only; public /home/**).
     * @returns {Promise<{ success: boolean, data?: { content, totalElements, totalPages }, error?: string }>}
     */
    list: async (page = 0, pageSize = 100, sort = "name:asc", category = null) => {
        try {
            const params = new URLSearchParams();
            params.append("page", String(page));
            params.append("pageSize", String(pageSize));
            params.append("sort", sort);
            if (category) params.append("category", category);
            const response = await ApiService.getData(`${LIBRARY_BASE}?${params.toString()}`);
            const parsed = parseEnvelope(response);
            if (!parsed.success) return parsed;
            return { success: true, data: normalizeListResponse(response) };
        } catch (e) {
            Logger.error("AudioService.list:", e);
            return { success: false, error: e.message || "Failed to list audio" };
        }
    },

    /** Playable rows for a category (URL present, not explicitly disabled). */
    listPlayable: async (category = "BGM", page = 0, pageSize = 100, sort = "name:asc") => {
        const result = await AudioService.list(page, pageSize, sort, category);
        if (!result.success) return result;
        const content = (result.data?.content ?? []).filter(isAudioRowPlayable);
        return {
            success: true,
            data: {
                ...result.data,
                content,
            },
        };
    },
};

export const AUDIO_EFFECT = Object.freeze({
    SLIDING_MOVE: "slidingMove",
    SLIDING_CELL_MATCH: "slidingCellMatch",
    SLIDING_LINE_MATCH: "slidingLineMatch",
    SLIDING_BONUS: "slidingBonus",
    JIGSAW_PIECE_SNAP: "jigsawPieceSnap",
    WORD_RESOLVED: "wordResolved",
    APPLAUSE: "applause",
});

export const AUDIO_CATEGORY = Object.freeze({
    BGM: "BGM",
    EFFECT: "EFFECT",
});

export default AudioService;
